/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// GradientLineChart configurations
import configs from "examples/Charts/LineCharts/GradientLineChart/configs";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";
import colorsDark from "assets/theme/base/ColorsDark";
import { useSystemTheme } from "context/SystemThemeContext";

function GradientLineChart({ title = "", description = "", height = "300px", chart }) {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState({});
  const { data, options } = chartData;
  
  // Use appropriate colors based on theme
  const themeColors = isDarkMode ? colorsDark : colors;

  useEffect(() => {
    const chartDatasets = chart.datasets
      ? chart.datasets.map((dataset) => ({
        ...dataset,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 3,
        borderColor: themeColors[dataset.color]
          ? themeColors[dataset.color || "dark"].main
          : themeColors.dark.main,
        fill: true,
        maxBarThickness: 6,
        backgroundColor: gradientChartLine(
          chartRef.current?.children[0],
          themeColors[dataset.color] ? themeColors[dataset.color || "dark"].main : themeColors.dark.main
        ),
      }))
      : [];

    setChartData(configs(chart.labels || [], chartDatasets));
  }, [chart, isDarkMode, themeColors]);

  // Update chart options for dark mode
  const getChartOptions = () => {
    if (!options) return {};
    
    return {
      ...options,
      plugins: {
        ...options.plugins,
        legend: {
          ...options.plugins?.legend,
          labels: {
            ...options.plugins?.legend?.labels,
            color: isDarkMode ? "#e2e8f0" : undefined,
          },
        },
        tooltip: {
          ...options.plugins?.tooltip,
          backgroundColor: isDarkMode ? "#060C18" : "#fff",
          titleColor: isDarkMode ? "#e2e8f0" : "#1a202c",
          bodyColor: isDarkMode ? "#a0aec0" : "#67748e",
          borderColor: isDarkMode ? "#2d2d3f" : "#e0e0e0",
          borderWidth: 1,
        },
      },
      scales: {
        ...options.scales,
        x: {
          ...options.scales?.x,
          ticks: {
            ...options.scales?.x?.ticks,
            color: isDarkMode ? "#a0aec0" : "#67748e",
          },
          grid: {
            ...options.scales?.x?.grid,
            color: isDarkMode ? "#2d2d3f" : "#e9ecef",
          },
        },
        y: {
          ...options.scales?.y,
          ticks: {
            ...options.scales?.y?.ticks,
            color: isDarkMode ? "#a0aec0" : "#67748e",
          },
          grid: {
            ...options.scales?.y?.grid,
            color: isDarkMode ? "#2d2d3f" : "#e9ecef",
          },
        },
      },
    };
  };

  const renderChart = (
    <SoftBox p={2}>
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox mb={1}>
              <SoftTypography 
                variant="h6" 
                sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}
              >
                {title}
              </SoftTypography>
            </SoftBox>
          )}
          <SoftBox mb={2}>
            <SoftTypography 
              component="div" 
              variant="button" 
              fontWeight="regular" 
              sx={{ color: isDarkMode ? "#a0aec0" : "text" }}
            >
              {description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      ) : null}
      {useMemo(
        () => (
          <SoftBox ref={chartRef} sx={{ height }}>
            <Line data={data} options={getChartOptions()} />
          </SoftBox>
        ),
        [chartData, height, isDarkMode]
      )}
    </SoftBox>
  );

  return title || description ? (
    <Card sx={{ 
      backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
      transition: "background-color 0.3s ease",
      border: isDarkMode ? "1px solid #2d2d3f" : "none",
    }}>
      {renderChart}
    </Card>
  ) : renderChart;
}

// Typechecking props for the GradientLineChart
GradientLineChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
};

export default GradientLineChart;